import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { authService } from '@/services/authService'
import {
  LogOut,
  Menu,
  X,
  Plus,
  LayoutGrid,
  Calendar,
  ClipboardList,
  Users,
  Megaphone,
  CalendarPlus,
  Building2,
  Settings,
  DollarSign
} from 'lucide-react'
import { useState, useEffect } from 'react'
import OrgSwitcher from './OrgSwitcher'
import NotificationCenter from './notifications/NotificationCenter'

// Helper to convert hex to RGB components
const hexToRgb = (hex: string): string => {
  if (!hex || typeof hex !== 'string') return '0, 28, 40'
  const cleanHex = hex.startsWith('#') ? hex.slice(1) : hex
  if (cleanHex.length !== 6) return '0, 28, 40'

  const r = parseInt(cleanHex.slice(0, 2), 16)
  const g = parseInt(cleanHex.slice(2, 4), 16)
  const b = parseInt(cleanHex.slice(4, 6), 16)
  return `${r}, ${g}, ${b}`
}

export default function Layout() {
  const { currentOrg, userRole } = useAuthStore()
  const location = useLocation()
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Injeção de Cores Dinâmicas (Branding)
  useEffect(() => {
    const root = document.documentElement
    if (currentOrg?.branding?.colors?.primary) {
      root.style.setProperty('--primary-rgb', hexToRgb(currentOrg.branding.colors.primary))
    } else {
      root.style.setProperty('--primary-rgb', '0, 28, 40') // Default #001c28
    }

    // Opcional: Gerar uma cor secundária ou usar a do branding se existir
    if (currentOrg?.branding?.colors?.secondary) {
      root.style.setProperty('--secondary-rgb', hexToRgb(currentOrg.branding.colors.secondary))
    } else {
      root.style.setProperty('--secondary-rgb', '212, 169, 77') // Default #d4a94d
    }
  }, [currentOrg])

  const handleLogout = async () => {
    await authService.signOut()
    navigate('/login')
  }

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutGrid, minRole: 'member' },
    { path: '/communication', label: 'Comunicação', icon: Megaphone, minRole: 'member' },
    { path: '/reserve', label: 'Reservar Espaço', icon: CalendarPlus, minRole: 'member' },
    { path: '/bookings', label: 'Minhas Reservas', icon: Calendar, minRole: 'member' },
    { path: '/spaces', label: 'Gestão de Espaços', icon: Building2, minRole: 'admin' },
    { path: '/members', label: 'Gestão de Membros', icon: Users, minRole: 'admin' },
    { path: '/admin/bookings', label: 'Gestão das Reservas', icon: ClipboardList, minRole: 'admin' },
    { path: '/financial', label: 'Dashboard Financeiro', icon: DollarSign, minRole: 'admin' },
    { path: '/admin', label: 'Configurações', icon: Settings, minRole: 'member' },
  ]

  const filteredNavItems = navItems.filter(item => {
    if (item.minRole === 'member') return true
    return userRole === 'owner' || userRole === 'admin'
  })

  return (
    <div className="flex min-h-screen bg-slate-200">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 h-screen sticky top-0 bg-primary z-50">
        {/* Logo Section */}
        <div className="p-4 bg-white">
          <div className="flex items-center justify-center cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-28 overflow-hidden">
              <img src="https://apprub.com.br/space/logo.png" className="w-full h-full object-contain" alt="SpaceRub Logo" />
            </div>
          </div>
        </div>

        {/* Org Switcher & Actions */}
        <div className="px-4 py-2 flex items-center justify-between">
          <div className="flex-1 mr-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-white/60 mb-1 px-2">Organização</p>
            <OrgSwitcher />
          </div>
          <div className="mt-4">
            <NotificationCenter variant="sidebar" />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-1 overflow-y-auto">
          <div className="space-y-0.5">
            {filteredNavItems.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.path
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${isActive
                    ? 'bg-secondary text-white'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-white/50'}`} />
                  <span className="text-sm font-medium">{item.label}</span>
                </Link>
              )
            })}
          </div>
        </nav>

        {/* Logout Button */}
        <div className="p-4">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 rounded-lg border border-red-400/20 text-red-400 text-sm font-medium hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>Sair</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden bg-white border-b border-slate-200 h-16 flex items-center justify-between sticky top-0 z-40 px-4 shadow-sm">
          <div className="flex items-center">
            <div className="w-24 overflow-hidden">
              <img src="https://apprub.com.br/space/logo.png" className="w-full h-full object-contain" alt="SpaceRub Logo" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationCenter variant="header" />
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all active:scale-95"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6 md:p-12 max-w-7xl w-full mx-auto">
          {!currentOrg && location.pathname !== '/create-org' && (
            <div className="mb-12 bg-primary p-8 rounded-[3rem] border-4 border-secondary/30 flex flex-col md:flex-row items-center justify-between animate-in fade-in slide-in-from-top-6 duration-700 shadow-3xl shadow-primary/20 text-white gap-8">
              <div className="flex items-center space-x-6">
                <div className="w-16 h-16 rounded-[1.5rem] bg-secondary flex items-center justify-center text-white shadow-2xl shadow-secondary/40 ring-4 ring-white/10">
                  <Plus className="w-8 h-8" />
                </div>
                <div>
                  <p className="text-xl font-black uppercase italic tracking-tight">Crie sua Organização</p>
                  <p className="text-sm text-white/60 font-medium">Você precisa de uma organização para começar a gerenciar seus espaços.</p>
                </div>
              </div>
              <Link to="/create-org" className="bg-secondary text-white px-10 py-5 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] shadow-2xl shadow-secondary/40 hover:scale-105 transition-all active:scale-95">Começar Agora</Link>
            </div>
          )}
          <Outlet />
        </main>
      </div>

      {/* Mobile Drawer Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-primary/60 backdrop-blur-md" onClick={() => setMobileMenuOpen(false)}></div>
          <aside className="absolute inset-y-0 left-0 w-80 bg-primary shadow-2xl animate-in slide-in-from-left duration-500 flex flex-col">
            {/* Mobile Logo Section */}
            <div className="p-4 bg-white flex items-center justify-between">
              <div className="flex items-center justify-center flex-1">
                <div className="w-28 overflow-hidden">
                  <img src="https://apprub.com.br/space/logo.png" className="w-full h-full object-contain" alt="SpaceRub Logo" />
                </div>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            {/* Org Switcher */}
            <div className="px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-white/60 mb-2 px-2">Organização</p>
              <OrgSwitcher />
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-4 py-2 overflow-y-auto">
              <div className="space-y-1">
                {filteredNavItems.map((item) => {
                  const Icon = item.icon
                  const isActive = location.pathname === item.path
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors ${isActive
                        ? 'bg-secondary text-white'
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                        }`}
                    >
                      <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-white/50'}`} />
                      <span className="text-sm font-medium">{item.label}</span>
                    </Link>
                  )
                })}
              </div>
            </nav>

            {/* Logout Button */}
            <div className="p-4">
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 rounded-lg border border-red-400/20 text-red-400 text-sm font-medium hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Sair</span>
              </button>
            </div>
          </aside>
        </div>
      )}
    </div>
  )
}
